import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSectionTheme } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import FeedWidget from "@/components/FeedWidget";
import TopVotedWidget from "@/components/TopVotedWidget";
import SourcingRoiCard from "@/components/SourcingRoiCard";
import AIAccuracyCard from "@/components/AIAccuracyCard";
import DocsPendingCard from "@/components/DocsPendingCard";
import AdminDashboard from "@/pages/AdminDashboard";
import {
    Briefcase, Users, FileText, Plus, ArrowUpRight, Calendar as CalIcon, Bell,
    Phone, MessageSquare, AlertTriangle, TrendingUp, UserPlus, Video, MapPin,
    Clock, ListChecks, Sparkles,
} from "lucide-react";

function formatTime(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
}

function fromNow(iso) {
    if (!iso) return "";
    try {
        const ms = Date.now() - new Date(iso).getTime();
        const m = Math.floor(ms / 60000);
        if (m < 1) return "just now";
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        const d = Math.floor(h / 24);
        if (d < 30) return `${d}d ago`;
        return new Date(iso).toLocaleDateString();
    } catch { return ""; }
}

function Stat({ label, value, accent, hint, testId }) {
    return (
        <div className="border border-border bg-white p-4 sm:p-5" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{label}</div>
            <div className={`mt-2 font-display font-black text-2xl sm:text-3xl tracking-tight ${accent ? "text-ikb" : ""}`}>{value}</div>
            {hint && <div className="text-[10px] font-mono text-muted-foreground mt-1">{hint}</div>}
        </div>
    );
}

function Widget({ title, icon: Icon, count, action, children, testId }) {
    return (
        <section className="border border-border bg-white" data-testid={testId}>
            <header className="border-b border-border px-4 py-3 flex items-center justify-between bg-secondary/40">
                <div className="flex items-center gap-2 min-w-0">
                    {Icon && <Icon className="w-4 h-4 text-ikb shrink-0" />}
                    <h3 className="font-display font-bold text-sm uppercase tracking-widest truncate">{title}</h3>
                    {typeof count === "number" && (
                        <span className="text-[10px] font-mono bg-ikb text-white px-1.5 py-0.5 rounded">{count}</span>
                    )}
                </div>
                {action}
            </header>
            <div className="max-h-[360px] overflow-y-auto divide-y divide-border">
                {children}
            </div>
        </section>
    );
}

function EmptyRow({ children }) {
    return <div className="px-4 py-6 text-xs text-foreground/60 text-center">{children}</div>;
}

function InterviewRow({ it }) {
    return (
        <Link
            to={it.candidate_id ? `/candidates/${it.candidate_id}` : "/calendar"}
            className="block px-4 py-3 hover:bg-secondary/50"
            data-testid={`dash-interview-${it.id}`}
        >
            <div className="flex items-center gap-3">
                <div className="text-[11px] font-mono uppercase tracking-widest text-ikb shrink-0 min-w-[78px]">
                    {formatTime(it.scheduled_at)}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{it.candidate?.name || "Unattached slot"}</div>
                    <div className="text-xs text-muted-foreground truncate">{it.job?.title || ""} · {it.round_name || `Round ${it.round_number || 1}`}</div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest shrink-0">
                    {it.online_offline === "online" || it.mode === "Video" ? (
                        <span className="inline-flex items-center gap-1 border border-border bg-secondary px-1.5 py-0.5"><Video className="w-3 h-3" /> Online</span>
                    ) : (
                        <span className="inline-flex items-center gap-1 border border-border bg-secondary px-1.5 py-0.5"><MapPin className="w-3 h-3" /> Offsite</span>
                    )}
                </div>
            </div>
        </Link>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    useSectionTheme("dashboard");
    const [data, setData] = useState(null);
    const [jobs, setJobs] = useState([]);

    useEffect(() => {
        if (user?.role === "admin") return;
        api.get("/dashboard/overview").then((r) => setData(r.data)).catch(() => setData({ stats: { jobs: 0, open_jobs: 0, candidates: 0, applications: 0 } }));
        api.get("/jobs").then((r) => setJobs(r.data || [])).catch(() => {});
    }, [user]);

    if (user?.role === "admin") {
        return <AdminDashboard />;
    }

    const stats = data?.stats || { jobs: 0, open_jobs: 0, candidates: 0, applications: 0 };
    const todayInt = data?.today_interviews || [];
    const upcoming = data?.upcoming_interviews || [];
    const reminders = data?.reminders || [];
    const followups = data?.recent_followups || [];
    const recentCands = data?.recent_candidates || [];
    const recentJobs = data?.recent_jobs || jobs.slice(0, 6);
    const slas = data?.open_slas || [];
    const unread = data?.unread_messages || 0;
    const outboundToday = data?.outbound_today || 0;

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-6">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3 sm:gap-4">
                <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Workspace</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">
                        Hi {user?.name?.split(" ")[0] || "there"} — here's your day.
                    </h1>
                    <p className="text-foreground/70 text-sm mt-1">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Link to="/calendar" className="hidden sm:inline-block">
                        <Button variant="outline" className="rounded-none h-10 border-border">
                            <CalIcon className="w-4 h-4 mr-2" /> Calendar
                        </Button>
                    </Link>
                    <Link to="/jobs/new">
                        <Button data-testid="dash-new-job" className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-10">
                            <Plus className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">New job</span>
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Top KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border">
                <Stat label="Today" value={todayInt.length} accent hint="Interviews" testId="dash-stat-today" />
                <Stat label="Open jobs" value={stats.open_jobs} testId="dash-stat-open-jobs" />
                <Stat label="Candidates" value={stats.candidates} testId="dash-stat-candidates" />
                <Stat label="Applications" value={stats.applications} testId="dash-stat-applications" />
                <Stat label="Outbound · today" value={outboundToday} hint="Calls / WA / Email" testId="dash-stat-outbound" />
                <Stat label="Unread DMs" value={unread} testId="dash-stat-unread" />
            </div>

            {/* Live activity feed */}
            <FeedWidget />

            {/* Doc-freshness alert — silent when nothing pending */}
            <DocsPendingCard />

            {/* Sourcing ROI — hires from Talent Pool outreach */}
            <SourcingRoiCard />

            {/* AI Accuracy — feedback-loop signal for last 7 days */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <AIAccuracyCard />
            </div>

            {/* Top-voted this week */}
            <TopVotedWidget />

            {/* Hero row: Today + Upcoming + Reminders */}
            <div className="grid lg:grid-cols-3 gap-4">
                <Widget title="Today's interviews" icon={CalIcon} count={todayInt.length} testId="dash-today" action={
                    <Link to="/calendar" className="text-[10px] font-mono uppercase tracking-widest text-ikb hover:underline">All →</Link>
                }>
                    {todayInt.length === 0 ? (
                        <EmptyRow>No interviews scheduled for today.</EmptyRow>
                    ) : todayInt.map((it) => <InterviewRow key={it.id} it={it} />)}
                </Widget>

                <Widget title="Upcoming · 7 days" icon={Clock} count={upcoming.length} testId="dash-upcoming">
                    {upcoming.length === 0 ? (
                        <EmptyRow>Nothing else this week.</EmptyRow>
                    ) : upcoming.slice(0, 6).map((it) => (
                        <Link key={it.id} to={it.candidate_id ? `/candidates/${it.candidate_id}` : "/calendar"} className="block px-4 py-3 hover:bg-secondary/50" data-testid={`dash-upcoming-${it.id}`}>
                            <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/70">
                                {new Date(it.scheduled_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · {formatTime(it.scheduled_at)}
                            </div>
                            <div className="text-sm font-medium truncate mt-1">{it.candidate?.name || "Unattached"}</div>
                            <div className="text-xs text-muted-foreground truncate">{it.job?.title || ""}</div>
                        </Link>
                    ))}
                </Widget>

                <Widget title="My reminders" icon={Bell} count={reminders.length} testId="dash-reminders" action={
                    <Link to="/calendar" className="text-[10px] font-mono uppercase tracking-widest text-ikb hover:underline">Add →</Link>
                }>
                    {reminders.length === 0 ? (
                        <EmptyRow>No reminders set. Add one from the calendar.</EmptyRow>
                    ) : reminders.slice(0, 6).map((r) => (
                        <div key={r.id} className="px-4 py-3" data-testid={`dash-reminder-${r.id}`}>
                            <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${r.color === "rose" ? "bg-rose-500" : r.color === "amber" ? "bg-amber-500" : r.color === "emerald" ? "bg-emerald-500" : r.color === "grey" ? "bg-slate-400" : "bg-ikb"}`} />
                                <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/70">
                                    {r.date}{r.time ? ` · ${r.time}` : ""}
                                </div>
                            </div>
                            <div className="text-sm font-medium mt-1 truncate">{r.title}</div>
                            {r.body && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{r.body}</div>}
                        </div>
                    ))}
                </Widget>
            </div>

            {/* Follow-ups + Recent candidates + Open SLAs */}
            <div className="grid lg:grid-cols-3 gap-4">
                <Widget title="Recent follow-ups" icon={MessageSquare} count={followups.length} testId="dash-followups">
                    {followups.length === 0 ? (
                        <EmptyRow>No follow-up activity yet.</EmptyRow>
                    ) : followups.slice(0, 6).map((f) => (
                        <Link key={f.id} to={`/candidates/${f.candidate_id}`} className="block px-4 py-3 hover:bg-secondary/50" data-testid={`dash-followup-${f.id}`}>
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-medium truncate">{f.candidate_name || "Candidate"}</div>
                                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 shrink-0">{fromNow(f.created_at)}</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                <span className="inline-block bg-secondary border border-border px-1.5 py-0.5 mr-1 text-[10px] uppercase">{(f.channel || "").toUpperCase()}</span>
                                {f.status}
                            </div>
                        </Link>
                    ))}
                </Widget>

                <Widget title="New candidates" icon={UserPlus} count={recentCands.length} testId="dash-recent-candidates">
                    {recentCands.length === 0 ? (
                        <EmptyRow>No candidates yet — upload a few resumes.</EmptyRow>
                    ) : recentCands.slice(0, 6).map((c) => (
                        <Link key={c.id} to={`/candidates/${c.id}`} className="block px-4 py-3 hover:bg-secondary/50" data-testid={`dash-cand-${c.id}`}>
                            <div className="text-sm font-medium truncate">{c.name || "Unnamed"}</div>
                            <div className="text-xs text-muted-foreground truncate">{c.current_title || ""}{c.current_company ? ` · ${c.current_company}` : ""}</div>
                            <div className="text-[10px] font-mono text-foreground/60 mt-0.5">{fromNow(c.created_at)}</div>
                        </Link>
                    ))}
                </Widget>

                <Widget title="Open SLAs" icon={AlertTriangle} count={slas.length} testId="dash-slas">
                    {slas.length === 0 ? (
                        <EmptyRow>No jobs nearing their due date.</EmptyRow>
                    ) : slas.slice(0, 6).map((j) => (
                        <Link key={j.id} to={`/jobs/${j.id}`} className="block px-4 py-3 hover:bg-secondary/50" data-testid={`dash-sla-${j.id}`}>
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-medium truncate">{j.title}</div>
                                <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 shrink-0 ${j.overdue ? "bg-rose-100 text-rose-700 border border-rose-300" : j.days_to_due <= 7 ? "bg-amber-100 text-amber-700 border border-amber-300" : "bg-secondary border border-border"}`}>
                                    {j.overdue ? `${Math.abs(j.days_to_due)}d over` : `${j.days_to_due}d left`}
                                </span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                                Due {new Date(j.due_date).toLocaleDateString()}{j.openings ? ` · ${j.openings} openings` : ""}
                            </div>
                        </Link>
                    ))}
                </Widget>
            </div>

            {/* Your jobs */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display font-bold text-xl flex items-center gap-2"><Briefcase className="w-5 h-5 text-ikb" /> Your jobs</h2>
                    <Link to="/jobs" className="text-sm text-ikb font-medium inline-flex items-center gap-1 hover:underline" data-testid="dash-view-all-jobs">
                        View all <ArrowUpRight className="w-3 h-3" />
                    </Link>
                </div>
                {recentJobs.length === 0 ? (
                    <div className="border border-dashed border-border p-12 text-center">
                        <Briefcase className="w-8 h-8 mx-auto text-muted-foreground mb-4" />
                        <h3 className="font-display font-bold text-lg">No jobs yet</h3>
                        <p className="text-sm text-muted-foreground mt-2">Create your first job to start screening candidates.</p>
                        <Link to="/jobs/new" className="mt-6 inline-block">
                            <Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white">Create job</Button>
                        </Link>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
                        {recentJobs.slice(0, 6).map((j) => (
                            <Link
                                key={j.id} to={`/jobs/${j.id}`}
                                data-testid={`dash-job-card-${j.id}`}
                                className="bg-white p-4 sm:p-5 hover:bg-secondary transition-colors group"
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <h3 className="font-display font-bold text-base leading-tight group-hover:text-ikb transition-colors">
                                        {j.title}
                                    </h3>
                                    <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-ikb transition-colors" />
                                </div>
                                <div className="flex items-center gap-3 text-xs font-mono text-muted-foreground mb-2 flex-wrap">
                                    <span>{j.location || "Remote"}</span>
                                    {j.work_mode && <><span>·</span><span>{j.work_mode}</span></>}
                                    {j.status && <><span>·</span><span className={`uppercase ${j.status === "active" ? "text-emerald-700" : "text-foreground/60"}`}>{j.status}</span></>}
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
